/**
 * Página de Busca de Estabelecimentos
 */

import React, { useState } from 'react';
import { Box, Paper, Typography, Button, Snackbar, Alert } from '@mui/material';
import { GroupAddOutlined } from '@mui/icons-material';
import MainLayout from '../components/Layout/MainLayout';
import MapComponent from '../components/Map/MapComponent';
import ControlPanel from '../components/Controls/ControlPanel';
import ResultsList from '../components/Results/ResultsList';
import ImportGroupModal from '../components/Groups/ImportGroupModal';
import { validateConfig } from '../utils/config';
import { useAppSelector } from '../hooks/useAppSelector';
import { useAppDispatch } from '../hooks/useAppDispatch';
import { useAuth } from '../contexts/AuthContext';
import { createGroupAsync } from '../store/slices/groupsSlice';

const SearchPage: React.FC = () => {
  const dispatch = useAppDispatch();
  const { user } = useAuth();
  const searchResults = useAppSelector((state) => state.search.results);
  
  const [activeTab, setActiveTab] = useState<number>(0);
  const [resultsCount, setResultsCount] = useState<number>(0);
  const [selectedCount, setSelectedCount] = useState<number>(0);
  const [exportTrigger, setExportTrigger] = useState<{ type: 'csv' | 'json' } | null>(null);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  
  // Dados da última busca para nome do grupo
  const [searchCity, setSearchCity] = useState<string>('');
  const [searchNeighborhood, setSearchNeighborhood] = useState<string>('');
  const [searchSector, setSearchSector] = useState<string>('');
  
  // Estados dos filtros e ordenação (centralizados)
  const [sortBy, setSortBy] = useState<'name' | 'rating' | 'distance'>('name');
  const [filterQuality, setFilterQuality] = useState<number>(0);
  const [filterHasWebsite, setFilterHasWebsite] = useState<boolean>(false);
  const [filterHasWhatsapp, setFilterHasWhatsapp] = useState<boolean>(false);
  const [filterHasPhone, setFilterHasPhone] = useState<boolean>(false);
  const [filterHasEmail, setFilterHasEmail] = useState<boolean>(false);

  // Validar configuração ao iniciar
  React.useEffect(() => {
    if (!validateConfig()) {
      alert('Configuração inválida. Verifique o arquivo .env');
    }
  }, []);

  // Quando a busca iniciar, mudar automaticamente para a guia de tabela
  const handleSearchStart = () => {
    setActiveTab(1);
  };

  // Callback para capturar dados da busca
  const handleSearchData = (data: { city?: string; neighborhood?: string; sector?: string }) => {
    if (data.city) setSearchCity(data.city);
    if (data.neighborhood) setSearchNeighborhood(data.neighborhood);
    if (data.sector) setSearchSector(data.sector);
  };

  // Funções de exportação
  const handleExportCSV = () => {
    setExportTrigger({ type: 'csv' });
  };

  const handleExportJSON = () => {
    setExportTrigger({ type: 'json' });
  };

  // Callback para atualizar contagens
  const handleCountsChange = (results: number, selected: number) => {
    setResultsCount(results);
    setSelectedCount(selected);
  };

  const handleImportGroup = (groupName: string) => {
    if (searchResults.length > 0 && user) {
      dispatch(createGroupAsync({
        name: groupName,
        leads: searchResults,
        userId: user.uid
      }));
      setSnackbarMessage(`Grupo "${groupName}" importado com ${searchResults.length} leads!`);
      setSnackbarOpen(true);
    }
  };

  return (
    <MainLayout>
      <Box>
        {/* Header */}
        <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h4" sx={{ color: '#FFFFFF', fontWeight: 700, mb: 1 }}>
              Buscar Estabelecimentos
            </Typography>
            <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
              Encontre estabelecimentos comerciais por localização e setor
            </Typography>
          </Box>
          
          {searchResults.length > 0 && (
            <Button
              variant="contained"
              startIcon={<GroupAddOutlined />}
              onClick={() => setImportModalOpen(true)}
              sx={{
                background: 'linear-gradient(135deg, #FF6B35 0%, #FF8A5B 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #FF8A5B 0%, #FF6B35 100%)',
                  transform: 'translateY(-2px)',
                },
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(255, 107, 53, 0.3)',
              }}
            >
              Importar Grupo ({searchResults.length})
            </Button>
          )}
        </Box>

        {/* Painel de Controles */}
        <Box sx={{ mb: 2 }}>
          <ControlPanel 
            onSearchStart={handleSearchStart}
            onSearchData={handleSearchData}
            sortBy={sortBy}
            onSortChange={setSortBy}
            filterQuality={filterQuality}
            onFilterQualityChange={setFilterQuality}
            filterHasWebsite={filterHasWebsite}
            onFilterWebsiteChange={setFilterHasWebsite}
            filterHasWhatsapp={filterHasWhatsapp}
            onFilterWhatsappChange={setFilterHasWhatsapp}
            filterHasPhone={filterHasPhone}
            onFilterPhoneChange={setFilterHasPhone}
            filterHasEmail={filterHasEmail}
            onFilterEmailChange={setFilterHasEmail}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            onExportCSV={handleExportCSV}
            onExportJSON={handleExportJSON}
            resultsCount={resultsCount}
            selectedCount={selectedCount}
          />
        </Box>

        {/* Conteúdo Principal */}
        <Box sx={{ minHeight: 'calc(100vh - 350px)' }}>
          {/* Guia 1: Mapa */}
          {activeTab === 0 && (
            <Paper sx={{ height: '600px', overflow: 'hidden', backgroundColor: '#1a1a1a', border: '1px solid rgba(255, 107, 53, 0.1)' }}>
              <MapComponent />
            </Paper>
          )}

          {/* Guia 2: Tabela Completa */}
          {activeTab === 1 && (
            <Box>
              <ResultsList 
                tableOnly 
                sortBy={sortBy}
                filterQuality={filterQuality}
                filterHasWebsite={filterHasWebsite}
                filterHasWhatsapp={filterHasWhatsapp}
                filterHasPhone={filterHasPhone}
                filterHasEmail={filterHasEmail}
                onCountsChange={handleCountsChange}
                exportTrigger={exportTrigger}
                onExportComplete={() => setExportTrigger(null)}
              />
            </Box>
          )}
        </Box>
      </Box>

      {/* Modal de Importar Grupo */}
      <ImportGroupModal
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        onImport={handleImportGroup}
        leadsCount={searchResults.length}
        userName={user?.displayName || user?.email || ''}
        city={searchCity}
        neighborhood={searchNeighborhood}
        sector={searchSector}
      />

      {/* Snackbar de Sucesso */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="success"
          sx={{
            backgroundColor: '#1a1a1a',
            color: '#FFFFFF',
            border: '1px solid #4caf50',
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </MainLayout>
  );
};

export default SearchPage;
